import { Button, Tab, Tabs } from '@mui/material';
import clsx from 'clsx';
import React, { FC, useCallback, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import s from './MessageTemplateEditor.module.scss';

import { Flex, Item } from '#root/Components/Flex';
import { IphoneEditor } from '#root/Components/IphoneEditor';
import { Loading } from '#root/Components/Loading';
import { MacEditor } from '#root/Components/MacEditor';
import { useConfirm } from '#root/hooks/use-confirm';
import { useEditableParty } from '#root/hooks/use-party';
import { useTranslation } from '#root/hooks/use-translation';
import { getService } from '#root/store';
import { IMessageTemplate } from '#root/store/types';
import { Keys } from '#root/translations-keys';
import { DoneIcon, MailIcon, PhoneIcon } from '#root/utils/icons';
import { createFakeDelayPromise } from '#root/utils/wait';

export interface IProps {
  template: IMessageTemplate;
  onCreate: (id: IMessageTemplate['id']) => void;
  className?: string;
}

const detectIfNameHasBeenRemoved = (original: IMessageTemplate, changed: IMessageTemplate) => {
  if (
    original.email.toLowerCase().includes('((name))') &&
    !changed.email.toLowerCase().includes('((name))')
  ) {
    return true;
  }
  if (
    original.text.toLowerCase().includes('((name))') &&
    !changed.text.toLowerCase().includes('((name))')
  ) {
    return true;
  }

  return false;
};

export const MessageTemplateCreator: FC<IProps> = ({ template, onCreate, className }) => {
  const t = useTranslation();
  const party = useEditableParty();
  const [submitting, setSubmitting] = useState(false);
  const { isConfirmed } = useConfirm();

  const [tab, setTab] = useState<'email' | 'text'>('email');
  const [textDone, setTextDone] = useState(false);
  const [emailDone, setEmailDone] = useState(false);

  const {
    setError,
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<IMessageTemplate>({
    defaultValues: template,
    mode: 'onChange',
  });

  const changeTab = useCallback(
    (_: React.SyntheticEvent | null, newValue: 'email' | 'text') => {
      if (newValue === 'email' && tab === 'text') {
        setTextDone(true);
      } else if (newValue === 'text' && tab === 'email') {
        setEmailDone(true);
      }
      setTab(newValue);
    },
    [tab]
  );

  const onSubmit: SubmitHandler<IMessageTemplate> = async data => {
    if ((!data.id.startsWith('new-') && !isDirty) || submitting || !party.id) return;
    if (
      detectIfNameHasBeenRemoved(template, data) &&
      (await isConfirmed(Keys.admin.message.warning_name_removed, Keys.generic.save)) === false
    ) {
      setSubmitting(false);
      return;
    }
    const fakeDelay = createFakeDelayPromise(1000);
    setSubmitting(true);
    getService('messageTemplates')
      .save(party.id, data.id, data)
      .then(responseTemplate => {
        reset(responseTemplate);
        onCreate(responseTemplate.id);
      })
      .catch(async error => {
        if (error.response) {
          const body = await error.response.json();
          if (body.errors) {
            for (const [field, message] of Object.entries<string>(body.errors)) {
              setError(field as any, { type: 'custom', message });
            }
          } else if (body.message) {
            alert(body.message);
          }
        }
      })
      .finally(async () => {
        await fakeDelay;
        setSubmitting(false);
      });
  };

  const next = () => {
    setTimeout(() => {
      // Use timeout, otherwise the submit button will also get a pointer event
      if (tab === 'email') changeTab(null, 'text');
    }, 200);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={clsx(className, s.form)}>
      <div className={clsx(s.stackReverseMobile, s.tabs)}>
        <div className={s.creatorActions}>
          <Tabs variant="scrollable" value={tab} onChange={changeTab} className={s.tabs}>
            <Tab
              label={
                <Flex align="center" gap>
                  {emailDone ? <DoneIcon color="primary" /> : <MailIcon />}
                  {t(Keys.admin.message.tab.email)}
                </Flex>
              }
              value="email"
            />
            <Tab
              label={
                <Flex align="center" gap>
                  {textDone ? <DoneIcon color="primary" /> : <PhoneIcon />}
                  {t(Keys.admin.message.tab.text)}
                </Flex>
              }
              value="text"
            />
          </Tabs>
          <Item>
            {!emailDone ? (
              <Button
                id="next"
                type="button"
                onPointerUp={next}
                color="primary"
                variant="contained"
                disableElevation
              >
                {t(Keys.generic.next)}
              </Button>
            ) : (
              <Button
                id="submit"
                type="submit"
                disabled={!isValid || submitting}
                color="primary"
                variant="contained"
                disableElevation
              >
                {submitting ? <Loading /> : t(Keys.generic.create)}
              </Button>
            )}
          </Item>
        </div>
      </div>
      <Flex column align="center" className={s.formInner}>
        <div className={clsx(s.tabContent, tab === 'email' && s.activeTab)}>
          <MacEditor control={control} />
        </div>
        <div className={clsx(s.tabContent, tab === 'text' && s.activeTab)}>
          <IphoneEditor control={control} />
        </div>
      </Flex>
    </form>
  );
};
